<template>
    <Head title="Self-assessment" />
    <BaseLayout>
        <button
            v-if="!isGrouped && !showingIntro"
            :disabled="composerBusy"
            class="self-start body-small text-valence-grey-750 hover:text-valence-grey-850 disabled:opacity-30 flex items-center gap-1"
            @click="goBackToCycle">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
        </button>

        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <AssessmentIntroLanding v-else-if="showingIntro && intro" :intro="intro" @begin="dismissIntro" />
        <AssessmentGroupedSections
            v-else-if="isGrouped"
            :response-id="responseId"
            :questions="questions"
            :sections="sections"
            :allow-score-examples="allowScoreExamples"
            :allow-not-observed="allowNotObserved"
            @submitted="onGroupedSubmitted"
            @exit="exitToCycle" />
        <template v-else-if="currentQuestion">
            <header>
                <div v-if="currentTopSection" class="flex items-baseline justify-between gap-3 mb-3">
                    <h1 class="body-bold text-valence-grey-850 leading-tight">{{ currentTopSection.title }}</h1>
                    <AssessmentEyebrow v-if="currentChildSection">{{ currentChildSection.title }}</AssessmentEyebrow>
                </div>
                <div class="flex items-center justify-between details font-medium text-valence-grey-750 mb-3">
                    <AssessmentEyebrow>Your turn — how you see yourself</AssessmentEyebrow>
                    <span>{{ headerStatus }}</span>
                </div>
                <BaseProgressBar :value="progressPct" size="xs" :aria-label="`Progress: ${progressPct}%`" />
            </header>

            <SectionIntroCard v-if="showingSectionIntro && currentTopSection" :section="currentTopSection" :question-count="currentTopSectionQuestionCount" />

            <QuestionCard
                v-else
                :question="currentQuestion"
                :score="currentSavedScore"
                :text="currentTextDraft"
                :not-observed="currentSavedNotObserved"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isCurrentUnanswered"
                @update:score="setScore"
                @update:text="currentTextDraft = $event"
                @update:not-observed="setNotObserved"
                @update:busy="composerBusy = $event" />

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div class="flex items-center justify-between">
                <button
                    :disabled="(currentIndex === 0 && !showingSectionIntro) || saving || composerBusy"
                    class="body-small text-valence-grey-750 hover:text-valence-grey-850 disabled:opacity-30 flex items-center gap-1"
                    @click="goPrev">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Previous
                </button>

                <BaseButton v-if="showingSectionIntro" theme="primary" @click="dismissSectionIntro">Begin section</BaseButton>
                <BaseButton v-else-if="!isLast" theme="primary" :disabled="saving || composerBusy" @click="goNext">Next</BaseButton>
                <BaseButton v-else theme="primary" :disabled="submitting || saving || composerBusy" @click="submit">
                    {{ submitting ? "Submitting…" : "Submit" }}
                </BaseButton>
            </div>
            <div v-if="composerBusy && !showingSectionIntro" class="details text-valence-grey-750 text-right -mt-3">Stop recording to continue.</div>
        </template>
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="showSubmitConfirm"
        title="Submit your responses?"
        confirm-label="Submit responses"
        cancel-label="Keep editing"
        :confirm-disabled="submitting"
        :close-on-confirm="false"
        @confirm="confirmSubmit">
        <template #content>
            <div class="body text-valence-grey-750">Once you submit, <strong class="text-valence-grey-850">your responses are final and can't be changed.</strong></div>
        </template>
    </ConfirmationDialog>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, AssessmentIntro, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import AssessmentIntroLanding from "~vue/components/AssessmentIntroLanding.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import SectionIntroCard from "~vue/components/SectionIntroCard.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

const props = defineProps<{
    cycle_id: string;
}>();

const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const isGrouped = computed(() => displayMode.value === "grouped_by_section");
const intro = ref<AssessmentIntro | null>(null);
const showingIntro = ref(false);
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const showSubmitConfirm = ref(false);
const showingSectionIntro = ref(false);
// Mirrors the composer's recording/transcribing state so navigation buttons
// can be disabled while a recording is in flight (LAB-491).
const composerBusy = ref(false);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/self-response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || "Could not load self-assessment.";
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    displayMode.value = payload.display_mode ?? "one_per_page";
    intro.value = payload.intro ?? null;
    // Only show the intro on a fresh (unsubmitted) response so returning
    // participants aren't gated by it again.
    showingIntro.value = intro.value !== null && !payload.submitted_at;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
    if (shouldShowIntroFor(0)) {
        showingSectionIntro.value = true;
    }
    const answeredCount = payload.questions.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_VIEWED, {
        cycle_id: props.cycle_id,
        total_questions: payload.questions.length,
        answered_count: answeredCount,
    });
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const progressPct = computed(() => Math.round(((currentIndex.value + 1) / questions.value.length) * 100));

const sectionsByCode = computed(() => {
    const map: Record<string, SectionSummary> = {};
    for (const s of sections.value) {
        map[s.code] = s;
    }
    return map;
});

// Walk up parent_code chain to the top-level (depth=0) ancestor.
function topSectionForCode(code: string | null | undefined): SectionSummary | null {
    if (!code) {
        return null;
    }
    let current = sectionsByCode.value[code] ?? null;
    while (current && current.parent_code) {
        const parent = sectionsByCode.value[current.parent_code];
        if (!parent) {
            break;
        }
        current = parent;
    }
    return current ?? null;
}

const currentTopSection = computed<SectionSummary | null>(() => topSectionForCode(currentQuestion.value?.section_code));

const currentChildSection = computed<SectionSummary | null>(() => {
    const code = currentQuestion.value?.section_code;
    if (!code) {
        return null;
    }
    const direct = sectionsByCode.value[code] ?? null;
    if (!direct || !direct.parent_code) {
        return null;
    }
    return direct;
});

const currentTopSectionQuestionCount = computed<number>(() => {
    const topCode = currentTopSection.value?.code;
    if (!topCode) {
        return 0;
    }
    return questions.value.filter((q) => topSectionForCode(q.section_code)?.code === topCode).length;
});

const headerStatus = computed(() => {
    if (showingSectionIntro.value) {
        return "About to start this section";
    }
    return `Question ${currentIndex.value + 1} of ${questions.value.length}`;
});

function shouldShowIntroFor(index: number): boolean {
    const q = questions.value[index];
    if (!q || !q.section_code) {
        return false;
    }
    const topCode = topSectionForCode(q.section_code)?.code;
    if (!topCode) {
        return false;
    }
    if (index === 0) {
        return true;
    }
    const prev = questions.value[index - 1];
    const prevTopCode = topSectionForCode(prev?.section_code)?.code;
    return prevTopCode !== topCode;
}

function dismissSectionIntro() {
    showingSectionIntro.value = false;
}

function dismissIntro() {
    showingIntro.value = false;
}

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}

function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = "Could not save your answer. Please try again.";
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

/** Sync the current text draft and validate the current question. Returns true if valid. */
function validateCurrentQuestion(action: "continuing" | "submitting"): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        error.value = `Please answer this required question before ${action}.`;
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (currentIndex.value >= questions.value.length - 1) {
        return;
    }
    if (!validateCurrentQuestion("continuing")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value += 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showingSectionIntro.value = shouldShowIntroFor(currentIndex.value);
}

async function goPrev() {
    // If we're on the intro for the current section, "Previous" just dismisses
    // the intro and shows the section's first question — nothing to save.
    if (showingSectionIntro.value) {
        showingSectionIntro.value = false;
        return;
    }
    if (currentIndex.value === 0) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showingSectionIntro.value = false;
}

async function goBackToCycle() {
    if (!(await saveCurrent())) {
        return;
    }
    router.visit(`/reflections/360/${props.cycle_id}`);
}

const showValidation = ref(false);

const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

async function submit() {
    if (submitting.value) {
        return;
    }
    if (!validateCurrentQuestion("submitting")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not submit.";
            showSubmitConfirm.value = false;
            return;
        }
        const answeredCount = questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
            cycle_id: props.cycle_id,
            total_questions: questions.value.length,
            answered_count: answeredCount,
        });
        router.visit(`/reflections/360/${props.cycle_id}`);
    } finally {
        submitting.value = false;
    }
}

// Grouped renderer owns its own save/submit; the host keeps the submit
// analytics + redirect so behavior matches the one-per-page flow.
function onGroupedSubmitted() {
    const answeredCount = questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
        cycle_id: props.cycle_id,
        total_questions: questions.value.length,
        answered_count: answeredCount,
    });
    exitToCycle();
}

// The grouped renderer saves dirty answers before emitting `exit`, so the host
// only needs to redirect (no extra save like goBackToCycle does).
function exitToCycle() {
    router.visit(`/reflections/360/${props.cycle_id}`);
}
</script>
